// © Microsoft Corporation. All rights reserved.

import { AppError, SocialDataConnectorNotFound, SocialNoModuleName } from "@msnews/diagnostics";
import { BridgeEventNames, dispatchBridgeEvent } from "./Social.bridge";
import { ConnectorNamespaces, ExperienceIds } from "@msnews/experiences-constants";
import { DynamicReducer, IReducer, PageBase } from "@msnews/experiences-redux";
import { IRequestContext, InspectorResponse, ScreenWidth, appEnvironment, tryAddOrderedTelemetryChild } from "@msnews/core";
import { SocialDataActions, SocialDataConnector } from "@msnews/social-data-connector";

import { CardSizes } from "@msnews/card-properties";
import { ComponentConnector } from "@msnews/experiences-connector";
import { ExperienceInitialProps } from "@msnews/experiences-base";
import { RiverCardActions } from "@msnews/river-data-types";
import { SocialActions } from "./Social.actions";
import { SocialConfig } from "./Social.config";
import { SocialInspector } from "./Social.inspector";
import { SocialProps } from "./index";
import { SocialService } from "@msnews/social-data-service";
import { SocialState } from "./Social.state";
import { Store } from "redux";
import { appErrorLogger } from "./utils/AppErrorLogger";
import { enums } from "./utils/enums";
import { hasThemeBase } from "./utils/themeHelpers";

// A partial config to ensure loading of social data connector.
// This hard-coded config can be removed after making sure that all config documents contain the data connector.
const socialDataConnectorConfig: Partial<SocialConfig> = {
    dataConnectors: [
        {
            screenWidth: ScreenWidth.Any,
            children: [
                {
                    experienceType: ConnectorNamespaces.SocialData,
                    instanceId: "",
                    href: "cms/api/amp/experienceConfigIndex/BB1cKaeg"
                }
            ]
        }
    ]
};

/**
 * The connector for the Social Experience.
 */
export class SocialConnector extends ComponentConnector<SocialConfig, SocialState, SocialProps, any> {

    private socialDataConnector: SocialDataConnector;

    /** @inheritdoc */
    public constructor(namespace: string, parentNamespace: string, rootReducer: DynamicReducer, reducer: IReducer<SocialState>, store: Store<any>, config: SocialConfig, requestContext: IRequestContext) {
        super(namespace, parentNamespace, rootReducer, reducer, store, {...socialDataConnectorConfig, ...config}, requestContext);
    }

    /** @inheritdoc */
    public async onComponentConnect(connectorProps?: ExperienceInitialProps): Promise<void> {
        if (!appErrorLogger.onAppError) {
            appErrorLogger.setOnAppError(this.onAppError.bind(this));
        }
        await ComponentConnector.prototype.onComponentConnect.call(this, connectorProps);

        const market = connectorProps && connectorProps.context && connectorProps.context.market ? connectorProps.context.market : appEnvironment.CurrentMarket;
        const moduleName = connectorProps && connectorProps.context && connectorProps.context.moduleName || connectorProps.context && this.config.moduleName;
        if (!moduleName) {
            this.onAppError({
                ...SocialNoModuleName,
                message: "No moduleName info not passed into the experience via connection props",
                pb: {
                    ...SocialNoModuleName.pb,
                    customMessage: JSON.stringify(connectorProps)
                }
            } as AppError);
        }

        PageBase.getInstance().rootReducer.getDataConnector(ConnectorNamespaces.SocialData).then((socialDataConnector: SocialDataConnector) => {
            if (socialDataConnector) {
                this.socialDataConnector = socialDataConnector;
                this.registerObservers();
                this.updateSocialState();

                this.setComponent(moduleName, market, connectorProps.context.theme, connectorProps.context.id, connectorProps.context.commentDisplayType, connectorProps.context.specificCommentId);

                if (connectorProps && connectorProps.context && connectorProps.context.gradientOverlayDataPromise) {
                    connectorProps.context.gradientOverlayDataPromise.then(gradientOverlayData => {
                        this.socialDataConnector.setGradientOverlayData(gradientOverlayData);
                    });
                }

                if (connectorProps && connectorProps.context && connectorProps.context.publisherSubscribeFollowButtonDataPromise) {
                    connectorProps.context.publisherSubscribeFollowButtonDataPromise.then(publisherSubscribeFollowButtonData => {
                        this.socialDataConnector.setPublisherSubscribeFollowButtonData(publisherSubscribeFollowButtonData);
                    });
                }
            } else {
                this.onAppError({
                    ...SocialDataConnectorNotFound,
                    message: "Social data connector not found in the root reducer",
                    pb: {
                        ...SocialDataConnectorNotFound.pb,
                        customMessage: "Social data connector not found in the root reducer"
                    }
                } as AppError);
            }
        });

        // content-preview cards use card-action experience on the publisher profile page
        const cardActionConfig = this.config.publisherProfileChildren && this.config.publisherProfileChildren.cardAction;
        if (cardActionConfig) {
            await this.loadChildExperience(cardActionConfig);
        }
    }

    public registerObservers(): void {
        SocialDataActions.setTheme.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateMe.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateNotifications.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateActivities.registerObserver(() => this.updateSocialState());
        SocialDataActions.deleteActivities.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateActivityStatus.registerObserver(() => this.updateSocialState());
        SocialDataActions.setContent.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateContent.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateContentRating.registerObserver(() => this.updateSocialState());
        SocialDataActions.insertComments.registerObserver(() => this.updateSocialState());
        SocialDataActions.resetSpecificCommentId.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateCommentRating.registerObserver(() => this.updateSocialState());
        SocialDataActions.incrementReplyCount.registerObserver(() => this.updateSocialState());
        SocialDataActions.insertComment.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateUser.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateUserFollowers.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateUserFollowing.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateUserComments.registerObserver(() => this.updateSocialState());
        SocialDataActions.deleteUserComment.registerObserver(() => this.updateSocialState());
        SocialDataActions.setReactionBreakdown.registerObserver(() => this.updateSocialState());
        SocialDataActions.setGradientOverlayData.registerObserver(() => this.updateSocialState());
        SocialDataActions.setPublisherSubscribeFollowButtonData.registerObserver(() => this.updateSocialState());
        SocialDataActions.setPopup.registerObserver(() => this.updateSocialState());
        SocialDataActions.addTyper.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateTypers.registerObserver(() => this.updateSocialState());
        SocialDataActions.recordSignalRHub.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateUGContents.registerObserver(() => this.updateSocialState());
        SocialDataActions.publishUGContent.registerObserver(() => this.updateSocialState());
        SocialDataActions.unPublishUGContent.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateResponsiveClass.registerObserver(() => this.updateSocialState());
        SocialDataActions.deleteMyData.registerObserver(() => this.updateSocialState());
        SocialDataActions.updateActivityAppeal.registerObserver(() => this.updateSocialState());
        SocialDataActions.mapCmsIdToContentId.registerObserver(() => this.updateSocialState());
        SocialDataActions.resetError.registerObserver(() => this.updateSocialState());
    }

    public updateSocialState(): void {
        if (this.getSocialDataConnector()) {
            SocialActions.setSocialData.getActionSender(this).send(this.socialDataConnector.getCurrentState());
        }
    }

    /** @inheritdoc */
    public mapStateToProps(state: SocialState): SocialProps {
        const inspectorResult: InspectorResponse<SocialProps> = SocialInspector.inspect({
            config: this.config,
            children: this.childComponentInstanceList,
            state: state
        });

        let connectorFunctions = {};
        if (this.getSocialDataConnector()) {
            connectorFunctions = this.socialDataConnector.getDataConnectorFunctionsWithStateBindings();
        }

        return {
            ...inspectorResult.props,
            ...this.telemetryTags,
            children: this.childComponentInstanceList,
            mainTelemetryObject: this.telemetryObject,
            componentConnector: {
                setComponent: this.setComponent.bind(this),
                getContentCards: this.getContentCards.bind(this)
            },
            ...connectorFunctions
        };
    }

    public async setComponent(component: string, market: string, theme: string, id?: string, commentDisplayType?: string, specificCommentId?: string) {
        const popup = this.getCurrentState().popup;
        if (popup && popup.moduleName) {
            this.socialDataConnector.closePopup(popup);
        }
        SocialActions.setComponent.getActionSender(this).send(component, id, market, specificCommentId, commentDisplayType);
        this.socialDataConnector.setTheme(theme);
        this.manageHistory(component, market, theme, id);
    }

    private manageHistory(component: string, market: string, theme: string, id?: string) {
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        if (isSuperApp) {
            if (history.state == null) {
                history.replaceState({...{component, id, market, theme, initial: true}}, "");
            } else if (component !== history.state.component || market !== history.state.market || theme !== history.state.theme || id !== history.state.id) {
                history.pushState({...{component, id, market, theme, initial: false}}, "");
                dispatchBridgeEvent(BridgeEventNames.increaseBackStack);
            }
            if (!window.onpopstate) {
                window.onpopstate = this.setHistoryComponent.bind(this);
            }
        }
    }

    private async setHistoryComponent(event) {
        this.setComponent(event.state.component, event.state.market, event.state.theme, event.state.id);
    }

    private getSocialDataConnector() {
        if (!this.socialDataConnector)  {
            this.onAppError({
                ...SocialDataConnectorNotFound,
                message: "Social data connector not found in the root reducer",
                pb: {
                    ...SocialDataConnectorNotFound.pb,
                    customMessage: "Social data connector not found in the root reducer"
                }
            } as AppError);
        }
        return this.socialDataConnector;
    }

    public async getContentCards(publisherId: string, market: string, premium?: boolean, args?: any) {
        const contentPreviewConfig = this.config.publisherProfileChildren && this.config.publisherProfileChildren.contentPreview;
        const cardData = await SocialService.getUserContents(publisherId, market, premium, args ? args: {});
        const cardType = premium ? "premium" : "all";

        const cardLoadInfo = [];
        cardData.items.forEach(card => {
            cardLoadInfo.push({
                initialProps: {
                    serviceContextMetadata: {metadata: {}, ...card},
                    parentTelemetry: this.telemetryObject,
                    experienceTelemetry: tryAddOrderedTelemetryChild(this.telemetryObject, ExperienceIds.contentPreview)
                },
                config: {
                    ...contentPreviewConfig,
                    instanceId: `${cardType}-contentPreview-${card.id}`
                }
            });
        });

        await Promise.all(cardLoadInfo.map(loadInfo => {
            if (!this.childMap.get(loadInfo.config.instanceId)) {  // if it is already loaded, do not load again
                return this.loadChildExperience(loadInfo.config, loadInfo.initialProps);
            }
        }));

        const cardConnectors = cardLoadInfo.map(
            loadInfo => this.childMap.get(loadInfo.config.instanceId)
        );
        cardConnectors.forEach(connector => {
            RiverCardActions.changeCardSize.getActionSender(connector).send(CardSizes.Size1Wx6H, 275, 325);
        });
        return cardConnectors.map(connector => connector.componentInstance);
    }
}